import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import Cal from "@calcom/embed-react";
import { getTherapistById, CAL_TEAM_SLUG } from "@/data/therapists";
import { trackEvent } from "@/lib/firebase";

export default function Booking() {
  const { id } = useParams();
  const therapist = getTherapistById(id);

  useEffect(() => {
    if (therapist?.calSlug) {
      trackEvent("booking_page_viewed", { therapist_id: therapist.id, therapist_name: therapist.name });
    }
  }, [therapist]);

  if (!therapist || !therapist.calSlug) {
    return (
      <div className="mx-auto max-w-screen-xl px-6 py-32 text-center" data-testid="booking-not-found">
        <h1 className="font-serif text-4xl">This booking link doesn't look right.</h1>
        <Link to="/find" className="mt-6 inline-flex items-center gap-2 font-semibold text-accent">
          <ArrowLeft className="h-4 w-4" /> Choose a therapist
        </Link>
      </div>
    );
  }

  const calLink = `${CAL_TEAM_SLUG}/${therapist.calSlug}`;

  return (
    <div className="mx-auto max-w-screen-lg px-6 py-16 md:py-24" data-testid="booking-page">
      <Link to={`/therapists/${therapist.id}`} data-testid="booking-back" className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground transition-colors hover:text-accent">
        <ArrowLeft className="h-4 w-4" /> Back to {therapist.first_name}'s profile
      </Link>

      <h1 className="mt-6 font-serif text-4xl font-bold tracking-tight md:text-5xl" data-testid="booking-title">
        A time that works for you, with {therapist.first_name}.
      </h1>
      <p className="mt-4 flex max-w-xl items-start gap-2 text-sm leading-relaxed text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
        Pick a slot below and complete payment securely — handled by our booking partner, Cal ID.
      </p>

      <div className="mt-10 overflow-hidden rounded-3xl border border-primary/10 bg-card">
        <Cal
          key={calLink}
          calLink={calLink}
          calOrigin="https://cal.id"
          embedJsUrl="https://cal.id/embed.js"
          data-testid="booking-embed"
          style={{ width: "100%", height: "720px", overflow: "auto" }}
          config={{ theme: "light" }}
        />
      </div>
    </div>
  );
}
